import { type Capability, carKeys } from '@taler/api-client'
import { defineEntryExtension, type RegisteredExtension, type RowView } from '@/features/entries'
import { useQueries, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useApi } from '@/app/api'
import CarEventRowDetail from '@/features/garage/components/CarEventRowDetail.vue'
import { canViewGarage } from '../internal/capabilities.ts'
import { CAR_EXPENSE_CODE, carCostFor } from '../internal/car-accounts.ts'
import { rememberDefaultAccountId } from '../internal/default-accounts.ts'
import { emptyEventData } from '../internal/event-data.ts'
import type {
  Car,
  CarEventData,
  CarEventType,
  InsurancePolicy,
  MaintenanceSchedule,
} from '../internal/types.ts'

export interface UseCarEventExtensionOptions {
  readonly ledgerId: () => string | undefined
  readonly can: (capability: Capability) => boolean
}

export interface CarEventMatch {
  readonly key: string
  readonly label: string
  readonly inferredType: CarEventType | undefined
  /** Unsold cars only: a sold car refuses every event but `SELL`. */
  readonly cars: readonly Car[]
  readonly policiesByCar: ReadonlyMap<string, readonly InsurancePolicy[]>
  readonly schedulesByCar: ReadonlyMap<string, readonly MaintenanceSchedule[]>
}

export interface CarEventDraft {
  readonly carId: string | undefined
  readonly type: CarEventType | undefined
  readonly data: CarEventData | undefined
  readonly mileageKm: string
}

export interface ResolvedCarEvent {
  readonly carId: string | undefined
  readonly type: CarEventType | undefined
  readonly data: CarEventData | undefined
}

/** What the draft amounts to: the only car preselected, the inferred type, data for that type. */
export function resolveCarEvent(match: CarEventMatch, draft: CarEventDraft): ResolvedCarEvent {
  const carId = draft.carId ?? (match.cars.length === 1 ? match.cars[0]!.id : undefined)
  const type = match.inferredType ?? draft.type
  const data = type === undefined ? undefined : draft.data?.type === type ? draft.data : emptyEventData(type)
  // An UNPAID or CONTESTED fine drops its journal link server-side; a posted one is paid.
  return { carId, type, data: data?.type === 'FINE' ? { ...data, status: 'PAID' } : data }
}

function carSplitTypes(view: RowView): readonly (CarEventType | undefined)[] | undefined {
  const costs = view.counters.map((target) =>
    target === undefined ? undefined : carCostFor(target.codePath),
  )
  const carCosts = costs.filter((cost) => cost !== undefined)
  return carCosts.length === 0 ? undefined : carCosts.map((cost) => cost.type)
}

/** A category under `EXPENSE.CAR` records a car event, linked to the entry once it posts. */
export function useCarEventExtension(options: UseCarEventExtensionOptions): RegisteredExtension {
  const api = useApi()
  const queryClient = useQueryClient()
  const { t } = useI18n()
  const enabled = computed(() => canViewGarage(options.can) && options.ledgerId() !== undefined)

  const carsQuery = useQuery({
    queryKey: computed(() => carKeys.list(options.ledgerId() ?? '')),
    queryFn: ({ signal }) => api.cars.list(options.ledgerId()!, signal),
    enabled,
    retry: false,
  })
  const unsold = computed(() => (carsQuery.data.value ?? []).filter((car) => car.status === 'ACTIVE'))

  const policiesQueries = useQueries({
    queries: computed(() =>
      unsold.value.map((car) => ({
        queryKey: carKeys.insurancePolicies(options.ledgerId() ?? '', car.id),
        queryFn: ({ signal }: { signal?: AbortSignal }) =>
          api.cars.listInsurancePolicies(options.ledgerId()!, car.id, signal),
        enabled: enabled.value,
        retry: false,
      })),
    ),
  })
  const schedulesQueries = useQueries({
    queries: computed(() =>
      unsold.value.map((car) => ({
        queryKey: carKeys.maintenanceSchedules(options.ledgerId() ?? '', car.id),
        queryFn: ({ signal }: { signal?: AbortSignal }) =>
          api.cars.listMaintenanceSchedules(options.ledgerId()!, car.id, signal),
        enabled: enabled.value,
        retry: false,
      })),
    ),
  })
  const policiesByCar = computed(
    () => new Map(unsold.value.map((car, index) => [car.id, policiesQueries.value[index]?.data ?? []])),
  )
  const schedulesByCar = computed(
    () => new Map(unsold.value.map((car, index) => [car.id, schedulesQueries.value[index]?.data ?? []])),
  )

  return defineEntryExtension<CarEventMatch, CarEventDraft>({
    id: 'car-event',
    detect: (view) => {
      if (!enabled.value || view.kind !== 'EXPENSE') return undefined
      const types = carSplitTypes(view)
      if (types === undefined) return undefined
      const inferredType = types.every((type) => type === types[0]) ? types[0] : undefined
      const parts = [
        t('garage.extension.label'),
        ...(inferredType === undefined ? [] : [t(`garage.eventType.${inferredType}`)]),
        ...(unsold.value.length === 1 ? [unsold.value[0]!.name] : []),
      ]
      return {
        key: inferredType ?? 'ask',
        label: parts.join(' · '),
        inferredType,
        cars: unsold.value,
        policiesByCar: policiesByCar.value,
        schedulesByCar: schedulesByCar.value,
      }
    },
    carries: new Set(['payee', 'memo', 'tags', 'splits']),
    owns: (target) => target.codePath.includes(CAR_EXPENSE_CODE),
    initialDraft: () => ({ carId: undefined, type: undefined, data: undefined, mileageKm: '' }),
    problems: (match, draft, view) => {
      if (carsQuery.isPending.value)
        return [{ severity: 'needs-details', message: t('garage.extension.loadingCars') }]
      if (match.cars.length === 0) return [{ severity: 'blocked', message: t('garage.extension.noCar') }]
      const hasNonCarSplit = view.counters.some(
        (target) =>
          target !== undefined && target.source === 'account' && carCostFor(target.codePath) === undefined,
      )
      if (hasNonCarSplit) return [{ severity: 'needs-details', message: t('garage.extension.ownRow') }]
      const resolved = resolveCarEvent(match, draft)
      return [
        ...(resolved.carId === undefined
          ? [{ severity: 'needs-details' as const, message: t('garage.extension.chooseCar') }]
          : []),
        ...(resolved.type === undefined
          ? [{ severity: 'needs-details' as const, message: t('garage.extension.chooseType') }]
          : []),
      ]
    },
    detail: CarEventRowDetail,
    placement: 'panel',
    submit: {
      shape: 'attach',
      run: async (posted, match, draft, view) => {
        const ledgerId = options.ledgerId()!
        const resolved = resolveCarEvent(match, draft)
        const mileage = draft.mileageKm.trim() === '' ? undefined : parseInt(draft.mileageKm, 10)
        const category = view.counters.find((target) => target !== undefined && carCostFor(target.codePath))
        await api.cars.recordEvent(ledgerId, resolved.carId!, {
          type: resolved.type!,
          date: view.row.date,
          data: resolved.data!,
          journal: {
            mode: 'LINK',
            entryId: posted.id,
            ...(category === undefined ? {} : { expenseAccountId: category.id }),
          },
          ...(mileage === undefined || Number.isNaN(mileage) ? {} : { mileageKm: mileage }),
        })
        if (category !== undefined) rememberDefaultAccountId(ledgerId, resolved.type!, category.id)
        for (const queryKey of carKeys.invalidatedByWrite(ledgerId)) {
          void queryClient.invalidateQueries({ queryKey })
        }
      },
    },
  })
}
